import { RRule } from 'rrule'
import type { RruleParams } from '@/shared/ui/rrule-builder'

// Parts of an RRULE that encode wall-clock time, not recurrence structure.
// RruleBuilder only handles structural parts; time is managed separately.
const TIME_PARTS_PATTERN = /;?(BYHOUR|BYMINUTE|BYSECOND)=[^;]*/gi

/**
 * Strips BYHOUR/BYMINUTE/BYSECOND from an RRULE string and returns both the
 * structural RRULE and the parsed wall-clock start time.
 *
 * BYHOUR=0;BYMINUTE=0 is preserved as startsAt='00:00' — midnight is explicit.
 */
export function splitRruleTime(rrule: string): { structuralRrule: string; startsAt: string } {
  const hourMatch = rrule.match(/BYHOUR=(\d+)/i)
  const minuteMatch = rrule.match(/BYMINUTE=(\d+)/i)

  const hasTime = hourMatch !== null || minuteMatch !== null
  const structuralRrule = rrule.replace(TIME_PARTS_PATTERN, '').replace(/;$/, '')

  if (!hasTime) {
    return { structuralRrule, startsAt: '' }
  }

  const hour = String(Number(hourMatch?.[1] ?? '0')).padStart(2, '0')
  const minute = String(Number(minuteMatch?.[1] ?? '0')).padStart(2, '0')
  return { structuralRrule, startsAt: `${hour}:${minute}` }
}

/**
 * Appends BYHOUR/BYMINUTE to a structural RRULE string.
 * Always writes explicit time — including midnight — so a reload reads back the same value.
 *
 * @param startsAt  "HH:MM" string; empty string leaves the RRULE unchanged.
 */
export function mergeRruleTime(structuralRrule: string, startsAt: string): string {
  if (!startsAt) return structuralRrule
  const [hourStr, minuteStr] = startsAt.split(':')
  const hour = Number(hourStr ?? 0)
  const minute = Number(minuteStr ?? 0)
  return `${structuralRrule};BYHOUR=${hour};BYMINUTE=${minute}`
}

/**
 * Parses a structural RRULE (no time components) into RruleParams for RruleBuilder,
 * using RRule.fromString().origOptions to avoid mode flips on reopen.
 *
 * Monthly parity with legacy:
 *   bymonthday present          → specific_day
 *   bysetpos + byweekday present → relative
 *   neither                     → none (anchor from first execution)
 */
export function parseRruleParams(structuralRrule: string): Partial<RruleParams> {
  if (!structuralRrule) return {}

  let parsed: RRule
  try {
    parsed = RRule.fromString(`RRULE:${structuralRrule}`)
  } catch {
    return {}
  }

  const opts = parsed.origOptions

  // Freq
  const freqMap: Record<number, string> = {
    [RRule.MINUTELY]: 'MINUTELY',
    [RRule.HOURLY]: 'HOURLY',
    [RRule.DAILY]: 'DAILY',
    [RRule.WEEKLY]: 'WEEKLY',
    [RRule.MONTHLY]: 'MONTHLY',
    [RRule.YEARLY]: 'YEARLY',
  }
  const freq = typeof opts.freq === 'number' ? (freqMap[opts.freq] ?? 'WEEKLY') : 'WEEKLY'
  const interval = typeof opts.interval === 'number' ? opts.interval : 1

  // Weekly
  const weeklyByweekday = opts.byweekday
  const hasWeeklyDays =
    freq === 'WEEKLY' && weeklyByweekday != null && Array.isArray(weeklyByweekday)
      ? weeklyByweekday.length > 0
      : false
  const weeklyMode: 'none' | 'days' = hasWeeklyDays ? 'days' : 'none'
  const selectedDays: string[] = hasWeeklyDays
    ? (weeklyByweekday as { weekday: number }[]).map((wd) => {
        const dayNames = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU']
        const idx = typeof wd === 'object' ? wd.weekday : wd
        return dayNames[idx] ?? 'MO'
      })
    : []

  // Monthly
  let monthlyMode: 'none' | 'specific_day' | 'relative' = 'specific_day'
  let monthlyDay = 1
  let monthlyPosition = 1
  let monthlyWeekday = 'MO'

  if (freq === 'MONTHLY') {
    const hasMonthDay = opts.bymonthday != null
    const hasSetpos = opts.bysetpos != null && opts.byweekday != null

    if (hasMonthDay) {
      monthlyMode = 'specific_day'
      const raw = Array.isArray(opts.bymonthday) ? opts.bymonthday[0] : opts.bymonthday
      monthlyDay = typeof raw === 'number' ? raw : 1
    } else if (hasSetpos) {
      monthlyMode = 'relative'
      const rawPos = Array.isArray(opts.bysetpos) ? opts.bysetpos[0] : opts.bysetpos
      monthlyPosition = typeof rawPos === 'number' ? rawPos : 1

      const wdRaw = Array.isArray(opts.byweekday) ? opts.byweekday[0] : opts.byweekday
      const dayNames = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU']
      const wdIdx = wdRaw != null && typeof wdRaw === 'object' ? wdRaw.weekday : wdRaw
      monthlyWeekday = typeof wdIdx === 'number' ? (dayNames[wdIdx] ?? 'MO') : 'MO'
    } else {
      monthlyMode = 'none'
    }
  }

  // Yearly
  let yearlyMode: 'none' | 'specific' = 'none'
  let yearlyDay = 1
  let yearlyMonth = 1

  if (freq === 'YEARLY') {
    if (opts.bymonth != null && opts.bymonthday != null) {
      yearlyMode = 'specific'
      const rawDay = Array.isArray(opts.bymonthday) ? opts.bymonthday[0] : opts.bymonthday
      const rawMonth = Array.isArray(opts.bymonth) ? opts.bymonth[0] : opts.bymonth
      yearlyDay = typeof rawDay === 'number' ? rawDay : 1
      yearlyMonth = typeof rawMonth === 'number' ? rawMonth : 1
    }
  }

  return {
    freq,
    interval,
    weeklyMode,
    selectedDays,
    monthlyMode,
    monthlyDay,
    monthlyPosition,
    monthlyWeekday,
    yearlyMode,
    yearlyDay,
    yearlyMonth,
  }
}

/**
 * rrule.js toText() silently ignores BYSETPOS, producing "every month on Thursday"
 * instead of "every month on the third Thursday". Converting BYSETPOS=N;BYDAY=WD to
 * positional BYDAY=NWD (RFC 5545 §3.3.10) gives toText() the form it understands.
 */
function toTextSafeRrule(structuralRrule: string): string {
  const bysetposMatch = structuralRrule.match(/BYSETPOS=(-?\d+)/i)
  const bydayMatch = structuralRrule.match(/BYDAY=([A-Z]{2})/i)
  if (!bysetposMatch || !bydayMatch) return structuralRrule
  const pos = bysetposMatch[1]
  const day = bydayMatch[1]
  return structuralRrule
    .replace(/;?BYSETPOS=-?\d+/i, '')
    .replace(/BYDAY=[A-Z]{2}/i, `BYDAY=${pos}${day}`)
    .replace(/^;/, '')
}

/**
 * rrule.js toText() may append locale wall-clock phrases ("at 2:30 PM") when BYHOUR
 * / BYMINUTE / DTSTART remain. Structural previews must stay time-free so Starts At
 * is owned by formatWallClockTime + tenantTimeFormat (E36-S3).
 *
 * Exported for unit-test parity with `test/frontend/frequencyTimeDisplay.test.mjs`.
 */
export const TO_TEXT_WALL_CLOCK_SUFFIX = /\s+at\s+\d{1,2}(?::\d{2})?(?::\d{2})?\s*(?:AM|PM)?/gi

export function stripWallClockFromToText(text: string): string {
  return text.replace(TO_TEXT_WALL_CLOCK_SUFFIX, '').trim()
}

/**
 * Returns a human-readable description of a potentially time-bearing RRULE string
 * without the noise introduced by BYHOUR/BYMINUTE components.
 */
export function humanReadableStructuralRrule(rrule: string): string {
  const { structuralRrule } = splitRruleTime(rrule)
  if (!structuralRrule) return rrule
  try {
    const text = RRule.fromString(`RRULE:${toTextSafeRrule(structuralRrule)}`).toText()
    return stripWallClockFromToText(text)
  } catch {
    return structuralRrule
  }
}

// Approximate hours per FREQ= unit — aligned with InstrumentTaskIntervalType seed catalog
// (Week=168, Month≈720, Year≈8765) so Frequency sort can compare Calendar Fixed tasks
// that store cadence in the RRULE and leave interval_hours null.
const RRULE_FREQ_HOURS: Record<string, number> = {
  MINUTELY: 1 / 60,
  HOURLY: 1,
  DAILY: 24,
  WEEKLY: 168,
  MONTHLY: 720,
  YEARLY: 8765,
}

/**
 * Estimates recurrence length in hours from an RRULE (FREQ × INTERVAL).
 * Used when interval_hours is null (typical for Calendar Fixed definitions).
 */
export function approximateIntervalHoursFromRrule(rrule: string | null | undefined): number | null {
  if (!rrule) return null

  const freqMatch = rrule.match(/FREQ=([A-Z]+)/i)
  const freq = freqMatch?.[1]?.toUpperCase()
  if (!freq) return null

  const hoursPerUnit = RRULE_FREQ_HOURS[freq]
  if (hoursPerUnit == null) return null

  const intervalMatch = rrule.match(/INTERVAL=(\d+)/i)
  const interval = intervalMatch ? parseInt(intervalMatch[1], 10) : 1
  if (!Number.isFinite(interval) || interval <= 0) return null

  return Math.max(1, Math.round(hoursPerUnit * interval))
}
